import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import DashboardLayout from '../components/dashboard/DashboardLayout';
import { usageAPI } from '../utils/api';
import { Filter, Download, CheckCircle, AlertCircle, Clock, Search } from 'lucide-react';
import { useTranslation } from 'react-i18next';

export default function Logs() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState({
    status: 'all',
    page: 1,
    limit: 25,
  });

  useEffect(() => {
    const fetchLogs = async () => {
      if (!isAuthenticated) {
        navigate('/login');
        return;
      }
      try {
        setLoading(true);
        const response = await usageAPI.getLogs({
          status: filters.status === 'all' ? undefined : filters.status,
          offset: (filters.page - 1) * filters.limit,
          limit: filters.limit,
        });
        setLogs(response.data.logs || []);
      } catch (error) {
        console.error('Failed to fetch logs:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchLogs();
  }, [isAuthenticated, navigate, filters]);

  const handleStatusChange = (status) => {
    setFilters({ ...filters, status, page: 1 });
  };

  const downloadLogs = async () => {
    try {
      // Use the export endpoint which returns all logs
      const response = await fetch(`/api/v1/logs/export`, {
        method: 'GET',
        headers: {
          'X-API-Key': localStorage.getItem('apiKey') || '',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to export logs');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `logs-export-${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to export logs:', error);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
          case 'sent':
          case 'not delivered':
        return <CheckCircle size={16} className="text-[#128C7E]" />;
          case 'failed':
            return <AlertCircle size={16} className="text-red-500" />;
      default:
        return <Clock size={16} className="text-[#A0AEC0]" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
          case 'sent':
          case 'not delivered':
        return 'bg-[#128C7E]/20 border-[#128C7E]/30 text-[#128C7E]';
          case 'failed':
            return 'bg-red-500/20 border-red-500/30 text-red-400';
      default:
        return 'bg-[#A0AEC0]/10 border-[#A0AEC0]/20 text-[#A0AEC0]';
    }
  };

  const filteredLogs = logs.filter(log =>
    log.phone.includes(searchQuery) ||
    log.message_id?.includes(searchQuery) ||
    log.otp_code?.includes(searchQuery)
  );

  return (
    <DashboardLayout>
      <div className="min-h-screen bg-[#0B0E14] p-4 sm:p-6 md:p-8">
        {/* Header Section */}
        <div className="mb-6 sm:mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 sm:gap-6">
            <div>
              <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-1 sm:mb-2">
                📊 {t('API Logs')}
              </h1>
              <p className="text-[#A0AEC0] text-xs sm:text-base">{t('View all OTP requests and delivery status in real-time')}</p>
            </div>
            <button
              onClick={downloadLogs}
              className="flex items-center justify-center gap-2 px-4 sm:px-6 py-2 sm:py-3 bg-[#128C7E] text-white rounded-lg sm:rounded-xl font-semibold text-sm sm:text-base hover:bg-[#0DA699] transition-all duration-300 hover:shadow-lg hover:shadow-[#128C7E]/20 whitespace-nowrap flex-shrink-0"
            >
              <Download size={16} className="sm:w-4.5 sm:h-4.5" />
              <span className="hidden sm:inline">{t('Export CSV')}</span>
              <span className="sm:hidden">{t('Export')}</span>
            </button>
          </div>
        </div>

        {/* Filters & Search Section */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 mb-6 sm:mb-8">
          {/* Search Bar */}
          <div className="relative">
            <Search className="absolute left-3 sm:left-4 top-1/2 -translate-y-1/2 text-[#A0AEC0] w-4 h-4 sm:w-4.5 sm:h-4.5" />
            <input
              type="text"
              placeholder={t('Search...')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full bg-[#1A1D23]/40 backdrop-blur-lg border border-[#2D3748] rounded-lg sm:rounded-xl pl-9 sm:pl-12 pr-3 sm:pr-4 py-2 sm:py-3 text-sm sm:text-base text-white placeholder-[#A0AEC0] focus:outline-none focus:border-[#128C7E] transition-colors"
            />
          </div>

          {/* Status Filter */}
          <div className="relative">
            <Filter className="absolute left-3 sm:left-4 top-1/2 -translate-y-1/2 text-[#A0AEC0] w-4 h-4 sm:w-4.5 sm:h-4.5" />
            <select
              value={filters.status}
              onChange={(e) => handleStatusChange(e.target.value)}
              className="w-full bg-[#1A1D23]/40 backdrop-blur-lg border border-[#2D3748] rounded-lg sm:rounded-xl pl-9 sm:pl-12 pr-3 sm:pr-4 py-2 sm:py-3 text-sm sm:text-base text-white focus:outline-none focus:border-[#128C7E] transition-colors appearance-none cursor-pointer"
            >
              <option value="all" className="bg-[#0B0E14]">{t('All Status')}</option>
                  <option value="sent" className="bg-[#0B0E14]">{t('Sent')}</option>
              <option value="failed" className="bg-[#0B0E14]">{t('Failed')}</option>
                  <option value="not delivered" className="bg-[#0B0E14]">{t('Not Delivered')}</option>
            </select>
          </div>
        </div>

        {/* Logs Container */}
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="text-center space-y-4">
              <div className="w-12 h-12 border-4 border-[#2D3748] border-t-[#128C7E] rounded-full animate-spin mx-auto"></div>
              <p className="text-[#A0AEC0]">{t('Loading logs...')}</p>
            </div>
          </div>
        ) : filteredLogs.length === 0 ? (
          <div className="bg-[#1A1D23]/40 backdrop-blur-lg border border-[#2D3748] rounded-lg sm:rounded-2xl p-6 sm:p-12 text-center">
            <div className="max-w-md mx-auto space-y-4">
              <div className="flex justify-center">
                <div className="w-12 h-12 sm:w-16 sm:h-16 bg-[#128C7E]/10 rounded-lg sm:rounded-2xl flex items-center justify-center border border-[#128C7E]/20">
                  <AlertCircle className="text-[#128C7E] w-6 h-6 sm:w-8 sm:h-8" />
                </div>
              </div>
              <h3 className="text-lg sm:text-xl font-semibold text-white">{t('No logs found')}</h3>
              <p className="text-xs sm:text-base text-[#A0AEC0]">{t('Start sending OTPs to see them here.')}</p>
            </div>
          </div>
        ) : (
          <>
            {/* Mobile Card View */}
            <div className="lg:hidden space-y-3 sm:space-y-4">
              {filteredLogs.map((log, index) => (
                <div
                  key={log.id || index}
                  className="bg-[#1A1D23]/40 backdrop-blur-lg border border-[#2D3748] rounded-lg p-4 hover:border-[#128C7E]/50 transition-colors"
                >
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-[#A0AEC0] text-xs font-semibold uppercase">{t('Phone')}</span>
                      <span className="font-semibold text-white font-mono text-sm">{log.phone}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-[#A0AEC0] text-xs font-semibold uppercase">{t('OTP')}</span>
                      <span className="font-mono text-[#128C7E] text-base font-bold">{log.otp_code || log.otp || log.code || '•••'}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-[#A0AEC0] text-xs font-semibold uppercase">{t('Status')}</span>
                      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold border ${getStatusColor(log.status)}`}>
                        {getStatusIcon(log.status)} {t(log.status)}
                      </span>
                    </div>
                    <div className="flex items-center justify-between pt-2 border-t border-[#2D3748]">
                      <span className="text-[#A0AEC0] text-xs font-semibold uppercase">{t('Date')}</span>
                      <span className="text-[#A0AEC0] text-xs">
                        {new Date(log.created_at).toLocaleString()}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Desktop Table View */}
            <div className="hidden lg:block bg-[#1A1D23]/40 backdrop-blur-lg border border-[#2D3748] rounded-2xl overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-[#2D3748]">
                      <th className="px-6 py-4 text-left text-xs font-semibold text-[#A0AEC0] uppercase tracking-wider">{t('Phone')}</th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-[#A0AEC0] uppercase tracking-wider">{t('OTP Code')}</th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-[#A0AEC0] uppercase tracking-wider">{t('Status')}</th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-[#A0AEC0] uppercase tracking-wider">{t('Message ID')}</th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-[#A0AEC0] uppercase tracking-wider">{t('Created At')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#2D3748]">
                    {filteredLogs.map((log, index) => (
                      <tr key={log.id || index} className="hover:bg-[#128C7E]/5 transition-colors">
                        <td className="px-6 py-4">
                          <span className="font-semibold text-white font-mono text-sm">{log.phone}</span>
                        </td>
                        <td className="px-6 py-4">
                          <span className="font-mono text-[#128C7E] text-base font-bold">{log.otp_code || log.otp || log.code || ''}</span>
                        </td>
                        <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          {getStatusIcon(log.status)}
                          <span className={`px-3 py-1 rounded-lg border text-xs font-semibold flex items-center gap-1 ${getStatusColor(log.status)}`}>
                            {log.status === 'sent' ? t('Delivered') : t(log.status.charAt(0).toUpperCase() + log.status.slice(1))}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-[#A0AEC0] font-mono text-xs break-all">{log.message_id || '—'}</span>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-[#A0AEC0] text-sm">
                          {new Date(log.created_at).toLocaleDateString()} 
                          <span className="text-xs text-[#A0AEC0]/70 block">{new Date(log.created_at).toLocaleTimeString()}</span>
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Footer with Results Count */}
            <div className="px-4 sm:px-6 py-3 sm:py-4 border-t border-[#2D3748] bg-[#0B0E14]/50">
              <p className="text-xs sm:text-sm text-[#A0AEC0]">
                {t('Showing')} <span className="font-semibold text-white">{filteredLogs.length}</span> {t('of')} <span className="font-semibold text-white">{logs.length}</span> {t('logs')}
              </p>
            </div>
            </div>
          </>
        )}
      </div>
    </DashboardLayout>
  );
}
